import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import Icon from 'ziphy-web-shared/basic/assets/images/Icon';
import { Button } from 'ziphy-web-shared/basic/lib/buttons';
import { Badge } from 'ziphy-web-shared/basic/lib/simple';
import { $router } from 'ziphy-web-shared/basic/utils/router';
import { formatDate } from 'ziphy-web-shared/basic/utils/time';
import { getDashboardIsStat, getDashboardProgramName, getDashboardStart, getDashboardStatus, getDashboardTests, getDashboardType, getTelehealthAppointmentId, } from '@nedoShared/modules/dashboard/dashboard.helper';
import styles from './ActivityCard.module.scss';
const ActivityCard = ({ item }) => {
    const { t } = useTranslation();
    const localTZ = moment.tz.guess();
    const renderIcon = (msg) => {
        const type = getDashboardType(msg);
        if (type === 'visit') {
            return _jsx(Icon, { type: 'calendarEvent', className: styles.image });
        }
        if (type === 'testsResults') {
            return _jsx(Icon, { type: 'tubes', className: styles.image });
        }
        return _jsx(Icon, { type: 'messageCircle', className: styles.image });
    };
    const renderType = (msg) => {
        const type = getDashboardType(msg);
        if (type === 'visit') {
            return t('dashboard.labels.newTelehealthVisit');
        }
        if (type === 'testsResults') {
            return t('dashboard.labels.testResultsAdded');
        }
        if (type === 'provider_assignment') {
            return t('dashboard.labels.newConsultation');
        }
        return null;
    };
    const renderProgram = (msg) => {
        const type = getDashboardType(msg);
        const date = formatDate(getDashboardStart(msg), 'datetimeFullNoYear', localTZ);
        if (type === 'visit') {
            return `${date} ${getDashboardProgramName(msg)}`;
        }
        if (type === 'testsResults') {
            return getDashboardTests(msg).join(', ');
        }
        return getDashboardProgramName(msg);
    };
    const renderButtonLabel = (msg) => {
        const type = getDashboardType(msg);
        if (type === 'visit') {
            return t('dashboard.actions.openVisit');
        }
        if (type === 'testsResults') {
            return t('dashboard.actions.checkResults');
        }
        return t('dashboard.actions.openConsultation');
    };
    const renderTime = (msg) => {
        return formatDate(getDashboardStart(msg), 'time', localTZ);
    };
    const renderBadges = (msg) => {
        const defaultMode = ['height16'];
        const badges = [];
        const today = moment.tz(localTZ).startOf('day');
        const yesterday = today.clone().subtract(1, 'day');
        const createDate = moment.tz(msg.created, localTZ);
        const createdToday = createDate.isSame(today, 'day');
        const createdYesterday = createDate.isSame(yesterday, 'day');
        if (getDashboardIsStat(msg)) {
            badges.push({ mode: ['stat', ...defaultMode], label: t('dashboard.labels.stat_uc') });
        }
        const st = getDashboardStatus(msg);
        if (st === 'new') {
            badges.push({ mode: ['dark-green', ...defaultMode], label: t('dashboard.labels.new') });
        }
        if (st === 'in_progress') {
            badges.push({ mode: ['gray-dark', ...defaultMode], label: t('dashboard.labels.inProgress') });
        }
        if (st === 'finished') {
            badges.push({ mode: ['gray-dark', ...defaultMode], label: t('dashboard.labels.finished') });
        }
        if (createdToday) {
            badges.push({
                mode: ['gray-dark', ...defaultMode],
                label: t('dashboard.labels.createdToday'),
            });
        }
        if (createdYesterday) {
            badges.push({
                mode: ['gray-dark', ...defaultMode],
                label: t('dashboard.labels.createdYesterday'),
            });
        }
        if (!createdToday && !createdYesterday) {
            badges.push({
                mode: ['light-red', ...defaultMode],
                label: `Created ${today.diff(createDate, 'days')}d ago`,
            });
        }
        return badges.map((el, i) => (
        // @ts-ignore
        _jsx(Badge, Object.assign({ mode: el.mode }, { children: el.label }), i)));
    };
    const handleOpenActivity = (msg) => {
        const type = getDashboardType(msg);
        if (type === 'visit') {
            const telehealthId = getTelehealthAppointmentId(msg.attachments);
            if (telehealthId) {
                $router.history.push(`/appointments/${telehealthId}`);
            }
            return;
        }
        if (type === 'provider_assignment') {
            $router.history.push(`/consultation/${msg.consultation._id}`);
            return;
        }
        if (type === 'testsResults') {
            const to = $router.toPath('consultation', {
                params: {
                    consultationId: msg.consultation._id,
                },
                query: {
                    openLabResults: msg.attachments[0].id,
                },
            });
            $router.history.push(to);
            return;
        }
    };
    return (_jsxs("div", Object.assign({ className: styles.card }, { children: [_jsxs("div", Object.assign({ className: styles.logo }, { children: [" ", renderIcon(item)] })), _jsxs("div", Object.assign({ className: styles.info }, { children: [_jsx("div", Object.assign({ className: styles.title }, { children: renderType(item) })), _jsx("div", { children: renderProgram(item) }), _jsx("div", Object.assign({ className: styles.button }, { children: _jsx(Button, Object.assign({ mode: ['xs', 'secondary-dark-outline'], onClick: () => handleOpenActivity(item) }, { children: renderButtonLabel(item) })) }))] })), _jsxs("div", Object.assign({ className: styles.status }, { children: [_jsx("div", { children: getDashboardProgramName(item) }), _jsx("div", Object.assign({ className: styles.badges }, { children: renderBadges(item) }))] })), _jsxs("div", Object.assign({ className: styles.time }, { children: [" ", renderTime(item)] }))] })));
};
export default observer(ActivityCard);
